<template lang="pug">
div
  div(
    v-if="badges.length > 0"
    :class="[isTrial ? 'trial-badges' : 'badges']"
  )
    div(v-for="badge in badges" :key="badge.key")
      StaticImage.img-badge(
        v-if="badge.img"
        :src="badge.img"
        :width="badge.width"
        :height="badge.height"
        :class="badge.modifiers"
        :alt="badge.text"
        :style="badge.style"
      )
      Badge(
        v-else
        :color="badge.color"
        :icon="badge.icon || 'shipdeal'"
        :class="badge.modifiers"
      )
        | {{ __(badge.text) }}
        span(v-html="badge.html" v-if="badge.html" )
  .five-for-five-disclaimer(v-if="show5For5Disclaimer")
    span.small *&nbsp;
    span.small {{ __('5 for $5 Available on Select Configurations') }}
  slot
</template>
<script setup lang="ts">
import { computed, type PropType } from 'vue'
import type { BuyRequest, BuyRequestMeta, ProductBadge } from '../../services/RogueTypes'
import { getBuyRequestMeta } from '../../helpers/buy-request'
import Badge from './badge.vue'
import { ALL_RACKBUILDER } from '@/js/stores/productConstants'
import { useProductStore } from '@/js/stores/productStore'
import { useRogue } from '@/js/services/Rogue'

const props = defineProps({
  buyRequest: {
    type     : Object as PropType<BuyRequest>,
    required : false,
  },
  meta: {
    type     : Object as PropType<BuyRequestMeta>,
    required : false,
  },
  parentMeta: {
    type     : Object as PropType<BuyRequestMeta>,
    required : false,
  },
  theme: {
    type     : String,
    required : false,
  },
  isCartShipsFreeProduct: {
    type    : Boolean,
    default : false,
  },
})

const productStore = useProductStore()
const rogue = useRogue()

const products = computed(() => productStore.products)

const fiveForFiveBlackImgSrc = computed(() => {
  switch (rogue.site.code) {
    case 'EUR':
      return '/UI/matte_black/5-for-5-EU-Black.svg'
    case 'GBR':
      return '/UI/matte_black/5-for-5-Pound-Black.svg'
    default:
      return '/UI/matte_black/5-for-5-US-Black.svg'
  }
})

const isTrial = computed(() => {
  if (props.meta) {
    return products.value[props.meta.id].attributes?.is_trial
  }

  try {
    const buyRequestMeta = getBuyRequestMeta(props.buyRequest!, rogue)

    return products.value[buyRequestMeta.id].attributes?.is_trial
  } catch (e) {
    return false
  }
})

const badges = computed(() => {
  if (props.buyRequest?.loading) {
    return []
  }
  let meta = props.meta

  if (!props.meta) {
    try {
      meta = getBuyRequestMeta(props.buyRequest!, rogue)
    } catch (e) {
      return []
    }
  }
  const parentFlags = props.parentMeta?.flags

  const badges:ProductBadge[] = []

  if (meta!.flags.hundo) {
    if (props.theme === 'small') {
      badges.push({
        img    : '/UI/logos/hundo-pricing_y6qzmi.svg',
        text   : 'Hundo Pricing',
        key    : 'hundo',
        width  : 132,
        height : 12,
        style  : { width: '132px', height: '12px' },
      })
    } else {
      badges.push({
        img    : '/UI/logos/hundo-pricing-item_b6ghpj.svg',
        text   : 'Hundo Pricing',
        key    : 'hundo',
        width  : 165,
        height : 12,
        style  : { width: '165px', height: '12px' },
      })
    }
  }

  if (meta!.flags.fiveForFive && !parentFlags?.fiveForFive) {
    const fiveForFive = { img    : fiveForFiveBlackImgSrc.value,
      text   : `5 Items Ship for ${rogue.currency(5, { hideFractionalUnits: true })}`,
      key    : '5for5',
      width  : 176,
      height : 84,
      style  : { width: '75px', height: '36px', 'margin-right': '4px' } }
    if (props.theme === 'small') {
      fiveForFive.style.width = '50px'
      fiveForFive.style.height = '25px'
    }
    badges.push(fiveForFive)
  }

  const product = products.value[meta!.id]

  if (meta!.flags.freeShip && !parentFlags?.freeShip && !props.isCartShipsFreeProduct && !product?.attributes.shipperhq_shipping_group?.includes('FREE_CART')) {
    // Grouped metas (the RHPA header on FXT pages) carry only groupId, so meta.id is undefined there despite its type.
    const zoneProduct = products.value[meta!.id ?? meta!.groupId]
    
    if (zoneProduct?.attributes.shipperhq_shipping_group?.find(group => ['EU_ZONE_1_FREE_SHIPPING', 'ZONE_1_FREE_SHIPPING']?.includes(group.toUpperCase()))) {
      badges.push({ text: 'Free Shipping - Zone 1', key: 'freeship', color: 'red' })
    } else {
      badges.push({ text: 'Free Shipping', key: 'freeship', color: 'red' })
    }
  }

  if (props.buyRequest?.metadata?.registry?.registryId) {
    badges.push({ text: 'Gear Registry', color: 'black', key: 'gearregistry' })
  }

  if (meta!.flags.free3 && !parentFlags?.free3) {
    badges.push({ color: 'red', icon: 'free-3', text: '3 Ships Free Item', key: 'free3' })
  }

  if (meta!.flags.freeCartShip && !parentFlags?.freeCartShip) {
    badges.push({ text: 'Cart Ships Free', key: 'cartfree' })
  }

  // if (!parentFlags && (meta.flags.heavyDirect || meta.flags.lightDirect)) {
  //   badges.push({ text: 'Ships from US', color: 'red', key: 'shipus' })
  // }

  if (product?.isProgram) {
    if (product?.attributes.program_delivery === 'BTWB') {
      badges.push({ text: '', icon: 'none', key: 'btwb', html: `<div class="badge-app-enabled">${rogue.__('App enabled')}:</div><img width="85" height="24" src="https://assets.roguefitness.com/f_auto,q_auto,w_190,h_48,b_rgb:ffffff/Rogue%20Invitational/2022/btwb_1_ry3m0i.png" alt="BTWB">` })
    } else if (product?.attributes.program_delivery === 'HWPO') {
      badges.push({ text: '', icon: 'none', key: 'hwpo', html: `<div class="badge-app-enabled">${rogue.__('App enabled')}:</div><img width="85" height="auto" src="https://assets.roguefitness.com/image/upload/v1707854998/catalog/Gear%20and%20Accessories/Books%20and%20Media/EBOOKS/HWPO0001%20-%20HWPO%20Flagship/logos/HWPO-training_aaftwe.png" alt="HWPO">` })
    }
  }

  const config = meta!.configId ? products.value[meta!.configId] : undefined
  const ticketRequired = product?.attributes?.ticket_required || config?.attributes?.ticket_required
  if (ticketRequired) {
    badges.unshift({ color: 'green', icon: 'gift-white', key: 'ticket-excl', text: 'Exclusive' })
    badges.push({ text: ticketRequired as string, icon: 'none', key: 'ticket' })
  }

  const customStock = meta!.customStockStatus || products.value[meta!.id || props.buyRequest?.productId as number]?.attributes.custom_stock_status

  if (meta!.leadTime && meta!.leadTime !== props.parentMeta?.leadTime) {
    badges.push({ text: meta!.leadTime, key: 'restockstatus' })
  } else if (customStock && customStock !== props.parentMeta?.customStockStatus) {
    if (customStock.indexOf('Purchased programs') === 0) {
      badges.push({ text: customStock, icon: 'none', key: 'program' })
    } else {
      // @TODO This is temporary to split custom_stock_status like "Ships in X duration.  Only available in US" into two badges.
      // A better solution would be to separate the two parts in Magento
      const stockStatus = customStock.split('. ')
      badges.push({ text: stockStatus[0], key: 'shipsin' })
      if (stockStatus.length > 1) {
        badges.push({ text: stockStatus.slice(1).join('. '), key: 'stockstatus', icon: 'none', modifiers: 'regular full-width' })
      }
    }
  }

  if (product?.attributes.is_trial && product?.attributes.trial_duration_days && props.theme === 'small') {
    badges.push({ text: `Free ${product.attributes.trial_duration_days}-Day Subscription Trial Included`, key: 'free-trial-included', icon: 'none' })
    badges.push({ text: 'Subscription Begins After Activation', key: 'free-trial-subscription', icon: 'none' })
  }

  if (meta?.type === 'grouped' && ALL_RACKBUILDER?.includes(meta?.linkSku?.toUpperCase()) && !props.isCartShipsFreeProduct) {
    badges.push({ text: 'This product can be viewed in 3D using your cursor or touch gestures.', key: '3D', icon: 'rackbuilder/360' })
  }

  return badges
})

const show5For5Disclaimer = computed<boolean>(() => {
  if (!rogue.featureFlags.isOn('fiveforfive_enabled')
    || !props.buyRequest
    || rogue.featureFlags.isOn('display_5for5_configurations')
    || !badges.value.find(badge => badge.key === '5for5')
    || products.value[props.buyRequest?.productId]?.type === 'grouped'
  ) {
    return false
  }

  const product = products.value[props.buyRequest?.productId]
  const children = product.childrenIds?.map(childId => products.value[childId]).filter(child => !!child)
  const blackFriday = children?.map(child => !!child.attributes.black_friday_dropship)

  return !!blackFriday?.some(isOn => !isOn) && blackFriday.some(isOn => isOn)
})
</script>
<style lang="scss" scoped>
.badges {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
}

.five-for-five-disclaimer span {
  font-style: italic;
}

.trial-badges {
  display: block;
}

.standard-badge {
  width: auto;
  height: $size-xxs;
  display: block;
  margin-bottom: $space-xxs;
}

.img-badge {
  margin: 0 8px 4px 0;
}

.badge:not(:last-child) {
  margin-bottom: $space-xxs;
}

.red {
  color: $color-red;
}

.small {
  font-size: $size-xxs;
}
</style>

<style lang="scss">
.badge-app-enabled {
  margin-bottom: $space-xxs;
}
</style>
